// Wraps ``<KindeProvider>`` and feeds the post-login redirect URL
// into ``KindeRedirectContext``.
//
// Mirrors ``frontends/shared-components/auth/KindeAuthenticatedUI.tsx``
// in shape; differences from admin-app:
//
//   - Audience is ``inrange-agents`` (admin-app uses ``inrange-api``).
//   - Tenant + client ID come from ``kindeConfig.ts`` which is fed
//     by ``VITE_KINDE_*`` env vars at build time.
//   - Auth domain is the bare Kinde tenant origin (e.g.
//     ``https://inrange.kinde.com``) - we have not yet wired the
//     ``auth.agents.inrange.io`` custom domain. Tokens issued here
//     still validate against the same JWKS the agents Lambda reads.
import { KindeProvider } from "@kinde-oss/kinde-auth-react";
import { useContext, type ReactNode } from "react";

import { KINDE_CONFIG } from "./kindeConfig";
import { KindeRedirectContext } from "./KindeRedirectContext";

export const KindeAuthenticatedUI = ({ children }: { children: ReactNode }) => {
  const { setRedirectUrl } = useContext(KindeRedirectContext);

  const redirectUri = `${window.location.protocol}//${window.location.host}/login-redirect`;
  // Trailing slash matches the pattern in the Kinde admin console's
  // "Allowed logout redirect URLs" list (``https://*.agents.inrange.dev/``
  // for the dev tenant, ``https://agents.inrange.io/`` for prod).
  // Kinde validates the SDK-supplied ``post_logout_redirect_uri``
  // against that allowlist with strict trailing-slash matching;
  // when it doesn't match, Kinde silently falls back to its own
  // ``/logged-out`` page rather than redirecting back to the app.
  const logoutUri = `${window.location.protocol}//${window.location.host}/`;

  return (
    <KindeProvider
      clientId={KINDE_CONFIG.clientId}
      domain={KINDE_CONFIG.domain}
      redirectUri={redirectUri}
      logoutUri={logoutUri}
      audience={KINDE_CONFIG.audience}
      // ``openid profile email offline`` so the JWT carries the
      // user's email claim. Without the ``email`` scope, the access
      // token Kinde mints has only ``sub`` (an opaque ``kp_...``
      // user id) and any BE that stamps ``updated_by`` / dismissed-by
      // shows the opaque id rather than ``noaa@inrange.io``.
      // ``offline`` keeps the refresh-token flow working as before.
      scope="openid profile email offline"
      onRedirectCallback={(_user, appState) => {
        const next =
          (appState && (appState as { redirectTo?: string }).redirectTo) ?? "/";
        setRedirectUrl(next);
      }}
    >
      {children}
    </KindeProvider>
  );
};
